import { Component, inject, OnInit, signal, effect, computed, input, untracked, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DashboardLayoutEditorComponent } from '../../../../shared/components/dashboard/dashboard-layout-editor.component';
import { ApiService } from '../../../../core/services/api.service';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import {
  DashboardPageKey,
  LendeeAnalyticsSectionId,
  LendeeAnalyticsSectionDef,
} from '../../../../core/enums/dashboard-cards.enum';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilterByWidgetKeyMap, findCatalogPageId, resolveActiveCatalogProductId } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  buildLayoutMapFromEditorDrafts,
  computeLayoutEditorAutoWidthUpdates,
  getLiveSectionColClass,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';

import { DatePickerComponent, type DateRangePickerOutput, type DateRangePickerValue } from '../../../../shared/components/date-picker/date-picker.component';
import { IndividualToasterComponent } from "../../../../shared/components/loader/loader.component";
import { HorizontalBarChartComponent } from '../../../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { FunnelChartComponent, IFunnelDataPoint } from '../../../../shared/components/charts/funnel-chart/funnel-chart.component';
import { DonutChartComponent, ChartData } from '../../../../shared/components/charts/donut-chart/donut-chart.component';
import { SingleBarComponent, BarChartData } from '../../../../shared/components/charts/bar-charts/single-bar/single-bar.component';
import { LineAreaChartComponent, ILineAreaSeries } from '../../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const LA_TIMEZONE = 'America/Los_Angeles';

import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LineChartComponent } from "../../../../shared/components/charts/line-chart/line-chart.component";
import { MultipleBarComponent } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { MultiBarGridChartComponent, ColorMap, LegendData } from '../../../../shared/components/charts/bar-charts/multi-bar-grid-chart/multi-bar-grid-chart';
import { DataProgressBarComponent, ProgressBarData } from '../../../../shared/components/data-progress-bar/data-progress-bar.component';
import { CircularProgressComponent } from '../../../../shared/components/circular-progress/circular-progress.component';
import { IconComponentV2 } from "../../../../shared/components/icon-component/icon.component";

interface IDeclineReason {
  reason_id: number;
  reason_text: string;
  total_count: number;
}

interface IRequestStats {
  total_requests: number;
  public_requests: number;
  private_requests: number;
  expired_requests: number;
  cancelled_requests: number;
  requests_cancelled_after_offer: number;
  request_to_offer: number;
  receivables_requests: number;
}

interface IOfferStats {
  total_offers: number;
  offers_accepted: number;
  offers_pending: number;
  cancelled_offers: number;
  offer_dropoff_rate: number;
  offer_acceptence_rate: number;
}

interface IPlanStats {
  plans_created: number;
  plans_completed_today: number;
  plans_overdue_today: number;
  plans_overdue_total: number;
  plan_created_in_recuvery?: number;
}

interface IMicrofundingStats {
  microfunding_total_count: number;
  lendee_profit_from_microfunding: number;
  microfunding_completed_contract: number;
  microfunding_completed_recuvery: number;
}

interface IOverdueStats {
  overdue_less_than_30: number;
  overdue_30_to_60: number;
  overdue_60_to_90: number;
  overdue_90_plus: number;
  overdue_receivables: number;
}

interface IReceivablesStats {
  receivables_total_count: number;
  receivables_funded: number;
  lendee_profit_from_receivable: number;
  receivables_completed_contract: number;
}

interface ITotalStats {
  total_count_partial_pay: number;
  total_amt_partial_pay: number;
  total_amt_recovery: number;
  total_verification_amt: number;
  total_refund_amt: number;
  total_combine_pay_user_count: number;
  total_refresh_pay_user_count: number;
}

interface IUserStats {
  supporter_count: number;
  recipient_count: number;
}

interface DailySummary {
  date: string;
  lacks_funds: number;
  cancelled_by_supporter: number;
  moved_to_collections: number;
  lendee_profit: number;
  decline_reasons: IDeclineReason[] | null;
  insights_stats: {
    value: string[];
  };
  request_stats: {
    value: IRequestStats;
  };
  offer_stats: {
    value: IOfferStats;
  };
  plans_stats: {
    value: IPlanStats;
  };
  microfunding_stats: {
    value: IMicrofundingStats;
  };
  overdue_stats: {
    value: IOverdueStats;
  };
  receivables_stats: {
    value: IReceivablesStats;
  };
  total_stats: {
    value: ITotalStats;
  };
  user_stats: {
    value: IUserStats;
  };
}

interface IDashboardResponse {
  data: DailySummary[];
  insights_stats?: {
    value: string[];
  };
  decline_reasons_data?: {
    value: IDeclineReason[];
  };
}

interface SectionLayoutItem {
  id: LendeeAnalyticsSectionId;
  visible: boolean;
  colSpan: number;
  order: number;
}

interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

interface MetricCard {
  title: string;
  value: string | number;
  icon: string;
  bgClass: string;
  iconColorClass: string;
  subValue?: string;
  isCurrency?: boolean;
}

@Component({
  selector: 'app-lendee-analytics-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    DashboardLayoutEditorComponent,
    DatePickerComponent,
    IndividualToasterComponent,
    HorizontalBarChartComponent,
    FunnelChartComponent,
    DonutChartComponent,
    SingleBarComponent,
    LineAreaChartComponent,
    MultipleBarComponent,
    DataProgressBarComponent,
    CircularProgressComponent,
    IconComponentV2
],
  templateUrl: './lendee-analytics-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [],
})
export class LendeeAnalyticsDashboardComponent implements OnInit {
  receivablesChartData = signal<any>(null);
  private chartContainerWidth = 0;
  private apiService = inject(ApiService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);
  readonly PAGE_KEY = DashboardPageKey.LENDEE_ANALYTICS;
  readonly SectionId = LendeeAnalyticsSectionId;
  readonly SECTIONS = LendeeAnalyticsSectionDef;

  isLoading = signal(false);
  showInsightsModal = signal(false);
  modalType = signal<'insights' | 'decline_reasons'>('insights');
  repaymentsMarkerLines: any[] = [];

  
  // Date Picker Configuration
  datePickerValue: DateRangePickerValue = {
    startDate: dayjs.utc().tz(LA_TIMEZONE).subtract(89, 'day').startOf('day').toISOString(),
    endDate: dayjs.utc().tz(LA_TIMEZONE).endOf('day').toISOString(),
    unit: 'week',
    comparisonType: 'none'
  };

  // --- Static Data ---

  // Top Metrics Rows
  metricCards = signal<MetricCard[]>([]);

  // Key Insights
  keyInsights: string[] = [];

  // Recipient Conversion Funnel
  funnelData: IFunnelDataPoint[] = [];

  // Request & Offer Performance
  requestOfferPerformanceData: ILineAreaSeries[] = [
    {
      name: 'Requests',
      data: [],
      lineColor: '#3b82f6',
      showPoints: true,
      areaOpacity: 0,
      key: 'requests'
    },
    {
      name: 'Offers',
      data: [],
      lineColor: '#f59e0b',
      showPoints: true,
      areaOpacity: 0,
      key: 'offers'
    }
  ];

  get requestsData() {
    return this.requestOfferPerformanceData[0].data.map((d) => ({
      ...d,
      start: d.date,
      end: d.date,
    }));
  }

  get offersData() {
    return this.requestOfferPerformanceData[1].data.map((d) => ({
      ...d,
      start: d.date,
      end: d.date,
    }));
  }

  // Recipient vs Supporter Signups
  signupsData: ILineAreaSeries[] = [
    {
      name: 'Recipient Signup',
      data: [],
      lineColor: '#3b82f6',
      areaColor: '#3b82f6',
      areaOpacity: 0.8,
      showPoints: false,
      key: 'recipient'
    },
    {
      name: 'Supporter Signup',
      data: [],
      lineColor: '#f59e0b',
      areaColor: '#f59e0b',
      areaOpacity: 0.8,
      showPoints: false,
      key: 'supporter'
    }
  ];

  // Request Status Summary
  requestStatusData: ChartData[] = [];

  // Offer Outcome Analysis
  offerOutcomeData: BarChartData[] = [];

  // Overdue & Collections Progress
  overdueProgressData: ChartData[] = [];

  // Partial Repayments on Overdue Loans
  partialRepaymentsData: ILineAreaSeries[] = [
    {
      name: 'Repayments',
      data: [],
      lineColor: '#3b82f6',
      showPoints: true,
      showValues: true,
      areaOpacity: 0,
      pointRadius: 4,
      key: 'repayments'
    }
  ];

  // Receivables Completion vs Revenue
  receivablesVsRevenueData: ILineAreaSeries[] = [
     {
      name: 'Receivables Completed',
      key: 'completed',
      data: [],
      lineColor: '#a855f7',
      renderAsBar: true,
      barWidth: 6,
      showValues: false
     },
     {
      name: 'Receivables Overdue',
      key: 'overdue',
      data: [],
      lineColor: '#3b82f6',
      renderAsBar: true,
      barWidth: 6,
      showValues: false
     },
     {
      name: 'Receivables Revenue',
      key: 'revenue',
      data: [],
      lineColor: '#10b981',
      renderAsBar: false,
      showPoints: true
     }
  ];


  // Receivables Recovery
  receivablesRecoveryData: any[] = [];

  // Transform receivables recovery data to progress bar format (percentages)
  get receivablesRecoveryProgressData(): ProgressBarData[] {
    if (this.receivablesRecoveryData.length === 0) return [];
    const maxValue = Math.max(...this.receivablesRecoveryData.map(d => d.value));
    return this.receivablesRecoveryData.map(item => ({
      label: item.label,
      value: Math.round((item.value / maxValue) * 100), // percentage for progress bar
      color: item.color,
      displayValue: item.value // show raw value instead of percentage
    }));
  }

  // Reasons List
  declineReasons: any[] = [];

  // Calculate progress percentage for decline reasons
  getDeclineReasonProgress(value: number): number {
    if (this.declineReasons.length === 0) return 0;
    const maxValue = Math.max(...this.declineReasons.map(r => r.value));
    return maxValue === 0 ? 0 : Math.round((value / maxValue) * 100);
  }

  // Data Presence Checkers (used for "No Data" screen logic)
  get hasOverdueData(): boolean {
    // Check for non-zero values and significant numeric presence
    return this.overdueProgressData.length > 0 && 
           this.overdueProgressData.some(d => typeof d.value === 'number' && d.value > 0);
  }

  get hasPartialRepaymentsData(): boolean {
    return this.partialRepaymentsData[0].data.length > 0 && 
           this.partialRepaymentsData[0].data.some(d => typeof d.value === 'number' && d.value > 0);
  }

  get hasRequestStatusData(): boolean {
    return this.requestStatusData.length > 0 && 
           this.requestStatusData.some(d => typeof d.value === 'number' && d.value > 0);
  }

  get hasOfferOutcomeData(): boolean {
    return this.offerOutcomeData.length > 0 && 
           this.offerOutcomeData.some(d => typeof d['count'] === 'number' && d['count'] > 0);
  }

  get hasReceivablesVsRevenueData(): boolean {
    return this.receivablesVsRevenueData.some(series => 
      series.data.length > 0 && series.data.some(d => typeof d.value === 'number' && d.value > 0));
  }

  get hasPerformanceData(): boolean {
    return this.requestOfferPerformanceData.some(series => 
      series.data.length > 0 && series.data.some(d => typeof d.value === 'number' && d.value > 0));
  }

  get hasSignupsData(): boolean {
    return this.signupsData.some(series => 
      series.data.length > 0 && series.data.some(d => typeof d.value === 'number' && d.value > 0));
  }

  // Responsive State
  donutLegendPosition: 'right' | 'bottom' = 'right';
  private breakpointObserver = inject(BreakpointObserver);

  readonly multipleBars = viewChildren(MultipleBarComponent);

  constructor() {
    effect(() => {
      this.multipleBars().forEach(chart => {
        chart.autoScrollOn5Years = true;
      });
    });
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      const productPid = this.apiService.selectedProductPid();
      if (!productId && productPid == null) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<LendeeAnalyticsSectionId>();
          keys.forEach((k) => {
            // Try the reverse map first so backend `le_ln_*` widget
            // keys hydrate against the matching `ld_*` section id.
            // Falls through to the legacy direct-match so any backend
            // entry that already uses the section enum value (e.g. a
            // future rename) keeps working.
            const mapped = this.WIDGET_KEY_TO_SECTION.get(k);
            if (mapped != null) {
              pinned.add(mapped);
              return;
            }
            if (Object.values(LendeeAnalyticsSectionId).includes(k as LendeeAnalyticsSectionId)) {
              pinned.add(k as LendeeAnalyticsSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<LendeeAnalyticsSectionId, number>();
            for (const sec of Object.values(LendeeAnalyticsSectionId)) {
              // Look up `widgets.id` using the backend `widget_key`
              // from {@link SECTION_WIDGET_KEYS} first. Falls back to
              // the raw `ld_*` section id so any future section added
              // with a matching backend key (no map entry needed)
              // still resolves.
              const mappedKey = this.SECTION_WIDGET_KEYS[sec];
              const id =
                (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    this.breakpointObserver.observe('(max-width: 1024px)')
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        this.donutLegendPosition = result.matches ? 'bottom' : 'right';
      });
  }

  ngOnInit(): void {
    this._initLayout();
    if (this.mixedMode()) {
      this.fetchDashboardData();
    }
  }

  // ─── Section layout (visibility + order; live page keeps original row grids) ─
  readonly layoutMap = signal<Record<LendeeAnalyticsSectionId, SectionLayoutItem>>(
    {} as Record<LendeeAnalyticsSectionId, SectionLayoutItem>,
  );

  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /**
   * Frontend section id (`ld_*`) → backend catalog `widget_key`
   * (`le_ln_*`) mapping. Backend catalog under product Lendee
   * (`pages.id = 38`, slug `analytical-dashboard`) registers one
   * widget per frontend section — keep in sync with the catalog.
   */
  private readonly SECTION_WIDGET_KEYS: Partial<Record<LendeeAnalyticsSectionId, string>> = {
    [LendeeAnalyticsSectionId.METRICS_OVERVIEW]:          'le_ln_operational_kpis',
    [LendeeAnalyticsSectionId.KEY_INSIGHTS]:              'le_ln_key_insights',
    [LendeeAnalyticsSectionId.RECIPIENT_FUNNEL]:          'le_ln_recipient_conversion_funnel',
    [LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE]: 'le_ln_request_offer_performance',
    [LendeeAnalyticsSectionId.SIGNUPS_TREND]:             'le_ln_signups_trend',
    [LendeeAnalyticsSectionId.REQUEST_STATUS]:            'le_ln_request_status_summary',
    [LendeeAnalyticsSectionId.OFFER_OUTCOME]:             'le_ln_offer_outcome_analysis',
    [LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS]:       'le_ln_overdue_collections_progress',
    [LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS]:        'le_ln_partial_repayments_trend',
    [LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE]:    'le_ln_receivables_completion_revenue',
    [LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY]:      'le_ln_receivables_recovery',
    [LendeeAnalyticsSectionId.DECLINE_REASONS]:           'le_ln_top_decline_reasons',
  };

  /**
   * Reverse lookup: backend `widget_key` → frontend section id.
   * Built once from {@link SECTION_WIDGET_KEYS}; used by mixed-mode
   * embedding in My Dashboard and by the hydration effect for pin state.
   */
  private readonly WIDGET_KEY_TO_SECTION: ReadonlyMap<string, LendeeAnalyticsSectionId> =
    new Map(
      (Object.entries(this.SECTION_WIDGET_KEYS) as [
        LendeeAnalyticsSectionId,
        string,
      ][]).map(([sec, key]) => [key, sec]),
    );

  readonly embeddedVisibleSections = createEmbeddedSectionFilterByWidgetKeyMap(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.WIDGET_KEY_TO_SECTION,
    this.orderedSections,
  );

  showSection(sectionId: LendeeAnalyticsSectionId): boolean {
    return this.embeddedVisibleSections().some((s) => s.id === sectionId);
  }

  sectionOrder(sectionId: LendeeAnalyticsSectionId): number {
    return this.layoutMap()[sectionId]?.order ?? 0;
  }

  getSectionColClass(sectionId: LendeeAnalyticsSectionId): string {
    if (this.mixedMode()) return 'col-span-12';
    const span =
      this.layoutMap()[sectionId]?.colSpan ??
      this.DEFAULT_COL_SPAN[sectionId] ??
      12;
    return getLiveSectionColClass(span);
  }

  /** Chart/metric card shell — drops duplicate chrome when embedded in My Dashboard. */
  chartSectionShellClass(extra = ''): string {
    const base = this.mixedMode()
      ? 'h-full flex flex-col min-h-0 overflow-hidden relative group'
      : 'bg-white dark:bg-surface-2 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm relative overflow-hidden group';
    return extra ? `${base} ${extra}` : base;
  }

  rowDisplayOrder(sectionIds: LendeeAnalyticsSectionId[]): number {
    const orders = sectionIds
      .filter((id) => this.showSection(id))
      .map((id) => this.sectionOrder(id));
    return orders.length ? Math.min(...orders) : 9999;
  }

  chartPairGridClass(sectionIds: LendeeAnalyticsSectionId[]): string {
    const visibleCount = sectionIds.filter((id) => this.showSection(id)).length;
    return visibleCount <= 1
      ? 'grid grid-cols-1 gap-6'
      : 'grid grid-cols-1 xl:grid-cols-2 gap-6';
  }

  readonly pinnedSections = signal<Set<LendeeAnalyticsSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<LendeeAnalyticsSectionId>>(new Set());

  readonly pinnableWidgetIds = signal<Map<LendeeAnalyticsSectionId, number>>(
    new Map(),
  );

  isAddedToCustomDashboard(sectionId: LendeeAnalyticsSectionId): boolean {
    return this.pinnedSections().has(sectionId);
  }

  isAddingToCustomDashboard(sectionId: LendeeAnalyticsSectionId): boolean {
    return this.pendingPinSections().has(sectionId);
  }

  addToCustomDashboard(sectionId: LendeeAnalyticsSectionId): void {
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const colSpan = this._normalizeColSpan(
      this.layoutMap()[sectionId]?.colSpan ??
        this.DEFAULT_COL_SPAN[sectionId] ??
        12,
    );
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);
    const productId = resolveActiveCatalogProductId(this.apiService);
    if (productId == null) return;

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const fire = (resolvedPageId: number) => {
      const widgetId = this.pinnableWidgetIds().get(sectionId);
      if (widgetId != null) {
        this.customDashboardPin.toggleLayoutItem({
          pageId: resolvedPageId,
          widgetId,
          sourcePageId: resolvedPageId,
          colSpan,
          rowSpan: 1,
          customConfig: {},
          productId,
          isCurrentlyPinned,
          onPinnedChange,
          onPendingChange,
        });
        return;
      }

      // Translate the frontend section id to the backend `widget_key`
      // before falling back to `toggleSection`. Sending the raw `ld_*`
      // id would trigger the backend's "Widget X is not registered in
      // the dashboards catalog" toast. Sections without a mapping
      // still send the raw id so future additions don't silently drop.
      this.customDashboardPin.toggleSection({
        pageId: resolvedPageId,
        widgetKey: this.SECTION_WIDGET_KEYS[sectionId] ?? sectionId,
        colSpan,
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
    };

    // Fast path — catalog `pages.id` already cached by the constructor
    // effect. Fires the API immediately, no additional round-trips.
    const cachedPageId = this.effectiveCatalogPageId();
    if (cachedPageId != null) {
      fire(cachedPageId);
      return;
    }

    // Fallback / slow path — pageId not yet resolved. Resolve the
    // catalog on-click so the API still fires.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId = findCatalogPageId(
          catalog as any[],
          this.PAGE_KEY,
          productId,
        );

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        // Cache so the fast path kicks in on the next click and the
        // existing hydration effect for "Added" pills stays consistent.
        this.effectiveCatalogPageId.set(resolvedPageId);

        // Resolve `widgets.id` map for this page so this click can
        // use the fast `toggleLayoutItem` variant. When the map
        // already has an entry from a prior effect run, this is
        // essentially free.
        if (this.pinnableWidgetIds().get(sectionId) == null) {
          this.customDashboardPin
            .resolveCatalogWidgetIdsForPage(resolvedPageId, productId)
            .subscribe((widgetIdMap) => {
              const next = new Map(this.pinnableWidgetIds());
              for (const id of Object.values(LendeeAnalyticsSectionId)) {
                // Mirror the constructor-effect lookup: try the
                // backend `widget_key` from {@link SECTION_WIDGET_KEYS}
                // first, then fall back to the raw `ld_*` id.
                const mappedKey = this.SECTION_WIDGET_KEYS[id];
                const wid =
                  (mappedKey ? widgetIdMap.get(mappedKey) : null) ??
                  widgetIdMap.get(id);
                if (wid != null) next.set(id, wid);
              }
              this.pinnableWidgetIds.set(next);
              fire(resolvedPageId);
            });
          return;
        }

        fire(resolvedPageId);
      });
  }

  private _warnCatalogMiss(
    catalog: any[],
    productId: string | number | null,
  ): void {
    const scopedProducts =
      productId != null && String(productId).trim() !== ''
        ? (catalog ?? []).filter(
            (p: any) => String(p?.id) === String(productId),
          )
        : (catalog ?? []);

    const productsForLog =
      scopedProducts.length > 0 ? scopedProducts : (catalog ?? []);

    const pageNames: string[] = [];
    for (const product of productsForLog) {
      for (const page of product?.pages ?? []) {
        const name = String(page?.name ?? '').trim();
        if (name) pageNames.push(name);
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[LendeeAnalyticsDashboard] No catalog page found for ` +
        `"${this.PAGE_KEY}" (active product=${productId}). ` +
        `Catalog advertised pages for this product: ` +
        `${pageNames.length ? pageNames.join(', ') : '(none)'}. ` +
        `If the correct page is in the list above, add a matching ` +
        `substring to CATALOG_PAGE_HINTS[LENDEE_ANALYTICS] ` +
        `in dashboard-embed.helpers.ts. If the list is empty, the ` +
        `backend has no catalog page for this report yet.`,
    );
  }

  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );
  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  private _layoutSnapshot: Record<LendeeAnalyticsSectionId, SectionLayoutItem> | null =
    null;

  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];
  private readonly LAYOUT_LS_KEY = 'prism_lendee_analytics_layout_v1';

  private readonly DEFAULT_COL_SPAN: Record<LendeeAnalyticsSectionId, number> = {
    [LendeeAnalyticsSectionId.METRICS_OVERVIEW]: 12,
    [LendeeAnalyticsSectionId.KEY_INSIGHTS]: 6,
    [LendeeAnalyticsSectionId.RECIPIENT_FUNNEL]: 6,
    [LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE]: 6,
    [LendeeAnalyticsSectionId.SIGNUPS_TREND]: 6,
    [LendeeAnalyticsSectionId.REQUEST_STATUS]: 6,
    [LendeeAnalyticsSectionId.OFFER_OUTCOME]: 6,
    [LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS]: 6,
    [LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS]: 6,
    [LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE]: 12,
    [LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY]: 6,
    [LendeeAnalyticsSectionId.DECLINE_REASONS]: 6,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      defaultColSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as LendeeAnalyticsSectionId] ?? 12,
      ),
    }));
    this.editorItems.set(items);
    this._layoutSnapshot = JSON.parse(JSON.stringify(this.layoutMap()));
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    if (this._layoutSnapshot) {
      this.layoutMap.set(this._layoutSnapshot);
      this._layoutSnapshot = null;
    }
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: LendeeAnalyticsSectionId): void {
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
    this._syncLayoutFromEditor();
  }

  resetLayoutEditorToDefaults(): void {
    this.editorItems.set(
      this.SECTIONS.map((s, idx) => {
        const defaultSpan = this._normalizeColSpan(
          this.DEFAULT_COL_SPAN[s.id as LendeeAnalyticsSectionId] ?? 12,
        );
        return {
          id: s.id as LendeeAnalyticsSectionId,
          title: s.title,
          visible: true,
          colSpan: defaultSpan,
          order: idx,
          defaultColSpan: defaultSpan,
        };
      }),
    );
    this._syncLayoutFromEditor();
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout = this._buildLayoutFromEditor();
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);
    this._layoutSnapshot = null;

    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via {@link CustomizeLayoutBatchService} — same flow as
   * Practina / Lendee Dashboard / Campaign Ads Stats.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    const toBackendKeysDraft = (resolvedPageId: number) =>
      draft.map((d, idx) => ({
        key: this.SECTION_WIDGET_KEYS[d.id] ?? String(d.id),
        sourcePageId: resolvedPageId,
        colSpan: this._normalizeColSpan(d.colSpan),
        visible: d.visible,
        customConfig: null,
        order: idx,
      }));

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d, idx) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: idx,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          this._warnCatalogMiss(catalog as any[], productId);
          return;
        }

        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: toBackendKeysDraft(resolvedPageId),
        });
      });
  }

  decreaseEditorCardColSpan(id: LendeeAnalyticsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[i.id] ?? 12);
        if (nextSpan < minSpan) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
    this._syncLayoutFromEditor();
  }

  increaseEditorCardColSpan(id: LendeeAnalyticsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
    this._syncLayoutFromEditor();
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
    this._syncLayoutFromEditor();
  }

  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    const minSpan = this._normalizeColSpan(this.DEFAULT_COL_SPAN[item.id] ?? 12);
    return this.COL_SPAN_STEPS[idx - 1] >= minSpan;
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }
  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  } 

  private _buildLayoutFromEditor(): Record<LendeeAnalyticsSectionId, SectionLayoutItem> {
    return buildLayoutMapFromEditorDrafts(
      this.editorItems(),
      this.SECTIONS as readonly { id: LendeeAnalyticsSectionId }[],
      this.DEFAULT_COL_SPAN,
      (span) => this._normalizeColSpan(span),
    );
  }

  private _syncLayoutFromEditor(): void {
    if (!this.isLayoutEditorOpen()) return;
    this.layoutMap.set(this._buildLayoutFromEditor());
  }

  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<LendeeAnalyticsSectionId, SectionLayoutItem> =
      {} as Record<LendeeAnalyticsSectionId, SectionLayoutItem>;
    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as LendeeAnalyticsSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12),
        order: storedItem?.order ?? idx,
      };
    });
    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(layout: Record<LendeeAnalyticsSectionId, SectionLayoutItem>): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // ignore
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  onDateRangeChange(event: DateRangePickerOutput): void {
    this.datePickerValue.startDate = event.start_date;
    this.datePickerValue.endDate = event.end_date;
    this.datePickerValue.unit = event.unit;
    this.fetchDashboardData();
  }

  fetchDashboardData(): void {
    this.isLoading.set(true);
    const payload = {
      start_date: dayjs(this.datePickerValue.startDate).tz(LA_TIMEZONE).format('MM/DD/YYYY'),
      end_date: dayjs(this.datePickerValue.endDate).tz(LA_TIMEZONE).format('MM/DD/YYYY'),
      metrics: [],
      is_dummy: 0
    };

    const headers = {
      'timezone': '+0000'
    };

    this.apiService
      .post<IDashboardResponse>('/admin/get_daily_summary', payload, {
        productServerName: 'lendee',
        addAccessToken: false,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
        headers: headers
      })
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          if (response) {
            this.mapApiResponseToView(response);
          }
        },
        error: (error) => {
          this.isLoading.set(false);
          console.error('Error fetching dashboard data:', error);
        }
      });
  }

  private aggregateDataByUnit(data: any[], unit: string, valuePath: string): { date: string, value: number, label: string }[] {
    if (!data || data.length === 0) return [];

    const groups: { [key: string]: { date: string, value: number, count: number } } = {};
    const dUnit = ['hour', 'day', 'week', 'month', 'quarter', 'year'].includes(unit) ? unit : 'day';

    data.forEach(item => {
      if (!item.date) return;
      const date = dayjs(item.date);
      const groupKey = date.startOf(dUnit as any).format('YYYY-MM-DD');
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          date: groupKey,
          value: 0,
          count: 0
        };
      }

      const value = valuePath.split('.').reduce((obj, key) => obj?.[key], item) || 0;
      groups[groupKey].value += value;
      groups[groupKey].count++;
    });

    return Object.values(groups)
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
      .map(group => ({
        date: group.date,
        value: group.value,
        label: this.getLabelByUnit(group.date, unit)
      }));
  }

  private getLabelByUnit(dateStr: string, unit: string): string {
    const date = dayjs(dateStr);
    const currentYear = dayjs().year();
    const isSameYear = date.year() === currentYear;

    switch (unit) {
      case 'hour':
        return date.format('HH:00');
      case 'day':
        return date.format(isSameYear ? 'MMM DD' : 'MMM DD YYYY');
      case 'week':
        const start = date.startOf('week');
        const end = date.endOf('week');
        return `${start.format('MMM DD')} - ${end.format('MMM DD')}`;
      case 'month':
        return date.format('MMM YYYY');
      case 'quarter':
        return `Q${Math.ceil((date.month() + 1) / 3)} ${date.year()}`;
      case 'year':
        return date.format('YYYY');
      default:
        return date.format('MMM DD');
    }
  }

  mapApiResponseToView(response: IDashboardResponse): void {
    const data = response.data;
    if (!data || data.length === 0) return;

    const unit = this.datePickerValue.unit || 'day';

    // Aggregations
    const totals = data.reduce((acc, day) => {
      acc.total_requests += day.request_stats.value.total_requests || 0;
      acc.total_offers += day.offer_stats.value.total_offers || 0;
      acc.plans_created += day.plans_stats.value.plans_created || 0;
      acc.microfunding_completed += day.microfunding_stats.value.microfunding_completed_contract || 0;
      acc.lendee_profit_microfunding += day.microfunding_stats.value.lendee_profit_from_microfunding || 0;
      acc.lendee_profit_receivable += day.receivables_stats.value.lendee_profit_from_receivable || 0;
      acc.recipient_signups += day.user_stats.value.recipient_count || 0;
      acc.supporter_signups += day.user_stats.value.supporter_count || 0;
      acc.receivables_completed += day.receivables_stats.value.receivables_completed_contract || 0;
      acc.total_amt_recovery += day.total_stats.value.total_amt_recovery || 0;
      acc.offers_accepted += day.offer_stats.value.offers_accepted || 0;
      acc.offers_pending += day.offer_stats.value.offers_pending || 0;
      acc.cancelled_offers += day.offer_stats.value.cancelled_offers || 0;
      acc.lacks_funds += day.lacks_funds || 0;
      
      // Request Status Categories
      acc.expired_requests += day.request_stats.value.expired_requests || 0;
      acc.cancelled_requests += day.request_stats.value.cancelled_requests || 0;
      acc.receivables_requests += day.request_stats.value.receivables_requests || 0;
      acc.receivables_funded += day.receivables_stats.value.receivables_funded || 0;
      acc.overdue_receivables += day.overdue_stats.value.overdue_receivables || 0;
      acc.refresh_paid_7 += day.total_stats.value.total_refresh_pay_user_count || 0;
      acc.cbc_refresh_count += day.total_stats.value.total_combine_pay_user_count || 0;
      acc.plans_overdue_total += day.plans_stats.value.plans_overdue_total || 0;
      acc.overdue_90_plus += day.overdue_stats.value.overdue_90_plus || 0;
      acc.moved_to_collections += day.moved_to_collections || 0;

      return acc;
    }, {
      total_requests: 0, total_offers: 0, plans_created: 0, microfunding_completed: 0,
      lendee_profit_microfunding: 0, lendee_profit_receivable: 0, recipient_signups: 0,
      supporter_signups: 0, receivables_completed: 0, total_amt_recovery: 0,
      offers_accepted: 0, offers_pending: 0, cancelled_offers: 0, lacks_funds: 0,
      expired_requests: 0, cancelled_requests: 0, receivables_requests: 0,
      receivables_funded: 0, overdue_receivables: 0, refresh_paid_7: 0,
      cbc_refresh_count: 0, plans_overdue_total: 0, overdue_90_plus: 0, moved_to_collections: 0
    });

    // Update Metric Cards
    this.metricCards.set([
      { title: 'Total Requests Created', value: totals.total_requests, icon: 'fileText', bgClass: 'bg-indigo-50 dark:bg-indigo-900/20', iconColorClass: 'text-[#38385B]' },
      { title: 'Total Offers Created', value: totals.total_offers, icon: 'gift', bgClass: 'bg-purple-50 dark:bg-purple-900/20', iconColorClass: 'text-[#AD46FF]' },
      { title: 'Payment Plans Created', value: totals.plans_created, icon: 'clipboardCheck', bgClass: 'bg-orange-50 dark:bg-orange-900/20', iconColorClass: 'text-[#FFA514]' },
      { title: 'Overdue Plans', value: totals.plans_overdue_total, icon: 'clipBoardMinus', bgClass: 'bg-red-50 dark:bg-red-900/20', iconColorClass: 'text-[#F05050]' },
      
      { title: 'Receivables Completed', value: totals.receivables_completed, icon: 'bookmarkCheck', bgClass: 'bg-blue-50 dark:bg-blue-900/20', iconColorClass: 'text-[#2188D2]' },
      { title: 'Recipent Paid $7', value: totals.refresh_paid_7, icon: 'circleCheck', bgClass: 'bg-teal-50 dark:bg-teal-900/20', iconColorClass: 'text-[#08BEB5]'},
      { title: 'CBC score $5 (Refresh every 60 days)', value: totals.cbc_refresh_count, icon: 'ChartNoAxesColumn', bgClass: 'bg-cyan-50 dark:bg-cyan-900/20', iconColorClass: 'text-[#0C66E8]' },
      { title: 'Microfinancing contract completed (Lender)', value: totals.microfunding_completed, icon: 'filecheck2', bgClass: 'bg-pink-50 dark:bg-pink-900/20', iconColorClass: 'text-[#EC4899]' },
      
      { title: 'Lendee Share from Microfinancing', value: totals.lendee_profit_microfunding, icon: 'CircleDollarSign', bgClass: 'bg-emerald-50 dark:bg-emerald-900/20', iconColorClass: 'text-[#15B268]', isCurrency: true },
      { title: 'Lendee Share from Receivables', value: totals.lendee_profit_receivable, icon: 'CircleDollarSign', bgClass: 'bg-rose-50 dark:bg-rose-600/20', iconColorClass: 'text-[#EC4899]', isCurrency: true },
      { title: 'Total Recipient Signups', value: totals.recipient_signups, icon: 'users', bgClass: 'bg-sky-50 dark:bg-sky-900/20', iconColorClass: 'text-[#48A2EC]' },
      { title: 'Total Supporter Signups', value: totals.supporter_signups, icon: 'users', bgClass: 'bg-indigo-50 dark:bg-indigo-900/20', iconColorClass: 'text-[#8293FF]' },
    ]);

    // Key Insights
    this.keyInsights = response.insights_stats?.value || [];

    // Conversion Funnel
    this.funnelData = [
      { label: 'Recipient Signups', value: totals.recipient_signups, percentage: 100, color: '#1779BF' },
      { label: 'Requests Created', value: totals.total_requests, percentage: 100, color: '#2A87D4' },
      { label: 'Offers Recieved', value: totals.total_offers, percentage: Math.round((totals.receivables_requests / totals.total_requests) * 100) || 0, color: '#05ACC9' },
      { label: 'Offers Accepted', value: totals.offers_accepted, percentage: Math.round((totals.total_offers / totals.total_requests) * 100) || 0, color: '#EF94C6' },
      { label: 'Payment Plans Created', value: totals.plans_created, percentage: Math.round((totals.plans_created / totals.total_requests) * 100) || 0, color: '#B87BED' },
    ];

    // Performance Charts
    this.requestOfferPerformanceData = [
      {
        ...this.requestOfferPerformanceData[0],
        data: this.aggregateDataByUnit(data, unit, 'request_stats.value.total_requests')
      },
      {
        ...this.requestOfferPerformanceData[1],
        data: this.aggregateDataByUnit(data, unit, 'offer_stats.value.total_offers')
      }
    ];

    this.signupsData = [
      {
        ...this.signupsData[0],
        data: this.aggregateDataByUnit(data, unit, 'user_stats.value.recipient_count')
      },
      {
        ...this.signupsData[1],
        data: this.aggregateDataByUnit(data, unit, 'user_stats.value.supporter_count')
      }
    ];

    // Chart 4: Request Status Summary
    this.requestStatusData = [
      { label: 'Active', value: totals.receivables_requests || 0, color: '#10b981' },
      { label: 'Expired', value: totals.expired_requests || 0, color: '#f59e0b' },
      { label: 'Post-offer', value: totals.total_offers || 0, color: '#a855f7' },
      { label: 'Cancelled', value: totals.cancelled_requests || 0, color: '#ef4444' }
    ];

    // Offer Outcome
    this.offerOutcomeData = [
      { label: 'Accepted', count: totals.offers_accepted, color: '#10b981', start: '', end: '' },
      { label: 'Pending', count: totals.offers_pending, color: '#f59e0b', start: '', end: '' },
      { label: 'Cancelled', count: totals.cancelled_offers, color: '#ef4444', start: '', end: '' },
      { label: 'Lacks Funds', count: totals.lacks_funds, color: '#3b82f6', start: '', end: '' }
    ];

    // Overdue Progress
    this.overdueProgressData = [
      { label: 'Currently Overdue', value: totals.plans_overdue_total, color: '#ef4444' },
      { label: 'Overdue 90+ Days', value: totals.overdue_90_plus, color: '#3b82f6' },
      { label: 'Moved to Collections', value: totals.moved_to_collections, color: '#f59e0b' },
      { label: 'Recovery Revenue', value: totals.total_amt_recovery, color: '#10b981' }
    ];

    // Partial Repayments
    this.partialRepaymentsData = [
      {
        ...this.partialRepaymentsData[0],
        data: this.aggregateDataByUnit(data, unit, 'total_stats.value.total_count_partial_pay')
      }
    ];

    // Receivables Chart
    this.receivablesVsRevenueData = [
      {
        ...this.receivablesVsRevenueData[0],
        data: this.aggregateDataByUnit(data, unit, 'receivables_stats.value.receivables_completed_contract').map(d => ({ ...d, start: d.date, end: d.date, key: 'Completed', type: 'bar' }))
      },
      {
        ...this.receivablesVsRevenueData[1],
        data: this.aggregateDataByUnit(data, unit, 'receivables_stats.value.receivables_total_count').map(d => ({ ...d, start: d.date, end: d.date, key: 'Overdue', type: 'bar' }))
      },
      {
        ...this.receivablesVsRevenueData[2],
        data: this.aggregateDataByUnit(data, unit, 'receivables_stats.value.lendee_profit_from_receivable').map(d => ({ ...d, start: d.date, end: d.date, key: 'Revenue', type: 'line' }))
      }
    ];

    // Update stable chart data property
    this.receivablesChartData.set({
      data1: this.receivablesVsRevenueData[0].data,
      data2: this.receivablesVsRevenueData[1].data,
      data3: this.receivablesVsRevenueData[2].data,
      data4: [],
      colors: {
        data1: '#a855f7', // Purple
        data2: '#3b82f6', // Blue
        data3: '#10b981', // Emerald (Line)
        data4: ''
      },
      tooltipData: [
        { label: 'Completed', key: 'Completed' },
        { label: 'Overdue', key: 'Overdue' },
        { label: 'Revenue', key: 'Revenue', type: '$' }
      ],
      legendData: [
        { label: 'Completed', color: '#a855f7' },
        { label: 'Overdue', color: '#3b82f6' },
        { label: 'Revenue', color: '#10b981' }
      ]
    });

    // Decline Reasons
    const reasons = response.decline_reasons_data?.value || [];
    this.declineReasons = reasons.map(r => ({
      label: r.reason_text,
      value: r.total_count,
      color: '#ef4444'
    })).sort((a, b) => b.value - a.value).filter((i) => i.value > 0);

    // Marker Lines (e.g., for May as in image 2)
    this.repaymentsMarkerLines = [
      { date: dayjs().month(4).startOf('month').format('YYYY-MM-DD'), lineStyle: 'dashed' }
    ];

    // Populating Receivables Recovery Data
    this.receivablesRecoveryData = [
      { label: 'Receivables Requests', value: Math.round(totals.receivables_requests), color: '#3b82f6' },
      { label: 'Receivables Funded', value: Math.round(totals.receivables_funded), color: '#06b6d4' },
      { label: 'Overdue Receivables', value: Math.round(totals.overdue_receivables), color: '#ef4444' },
      { label: 'Completed Contracts', value: Math.round(totals.receivables_completed), color: '#a855f7' }
    ];
  }

  toggleInsights(type: 'insights' | 'decline_reasons') {
    this.modalType.set(type);
    this.showInsightsModal.set(true);
  }

  closeInsightsModal() {
    this.showInsightsModal.set(false);
  }

}
